import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CustomerText, CustomerTextConversation, CustomerTextInput, OriginEntityType } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

/**
 * Wire shape of a `CustomerText` as it leaves the API: date fields are UTC ISO 8601 strings.
 * `HttpClient` does not revive dates, so the service converts them to `Date` (`toCustomerText`)
 * before the value reaches the typed `CustomerText` model consumers bind to.
 */
type CustomerTextWire = Omit<CustomerText, 'sentDate' | 'readDate'> & {
    sentDate: string;
    readDate: string | null;
};

/** Wire shape of a `CustomerTextConversation`: `latestSentDate` arrives as a UTC ISO 8601 string. */
type CustomerTextConversationWire = Omit<CustomerTextConversation, 'latestSentDate'> & {
    latestSentDate: string;
};

/**
 * Client for the two-way customer SMS/MMS messaging API (Spec #76394). Hand-written (no client
 * generator), following the customer.service.ts pattern: plain HttpClient plus ServiceUtils for base
 * URL construction. The controller is tenant-scoped only -- there is no branchId segment in any
 * route -- so every call resolves its base URL with `getBaseUrl('customertext', tenantId)`.
 */
@Injectable({
    providedIn: 'root',
})
export class CustomerTextApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private readonly httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    /**
     * Returns a customer's thread ordered by SentDate. When both origin arguments are supplied the
     * request is scoped to that topic so a screen can open on its own inspection / quote / work-order
     * conversation. A background read -- suppresses the global loading indicator.
     */
    getThread(tenantId: string, customerId: string, originEntityType?: OriginEntityType, originEntityId?: string): Observable<CustomerText[]> {
        let params = new HttpParams();
        if (originEntityId && originEntityType) {
            params = params.set('originEntityId', originEntityId).set('originEntityType', originEntityType);
        }

        return this.http
            .get<CustomerTextWire[]>(`${this.serviceUtils.getBaseUrl('customertext', tenantId)}/customer/${customerId}`, {
                headers: this.httpHeadersWithNoLoading,
                params,
            })
            .pipe(map((thread) => thread.map(CustomerTextApiService.toCustomerText)));
    }

    /**
     * Returns the phone-keyed thread for an unmatched conversation (CustomerId null), matched on the
     * normalized FromPhoneNumber. The phone number travels in a POST body rather than the URL path so
     * customer PII never appears in request logs or history. A background read -- suppresses the
     * global loading indicator.
     */
    getThreadByPhone(tenantId: string, phone: string, limit?: number): Observable<CustomerText[]> {
        const body: { phoneNumber: string; limit?: number } = { phoneNumber: phone };
        if (limit != null) {
            body.limit = limit;
        }
        return this.http
            .post<CustomerTextWire[]>(`${this.serviceUtils.getBaseUrl('customertext', tenantId)}/phone-thread`, body, {
                headers: this.httpHeadersWithNoLoading,
            })
            .pipe(map((thread) => thread.map(CustomerTextApiService.toCustomerText)));
    }

    /**
     * Returns the Message-Center left-list: one entry per conversation, newest-received-first. A
     * background read -- suppresses the global loading indicator.
     */
    getConversations(tenantId: string): Observable<CustomerTextConversation[]> {
        return this.http
            .get<CustomerTextConversationWire[]>(`${this.serviceUtils.getBaseUrl('customertext', tenantId)}/conversations`, {
                headers: this.httpHeadersWithNoLoading,
            })
            .pipe(map((conversations) => conversations.map(CustomerTextApiService.toConversation)));
    }

    /**
     * Sends and persists an outbound message for a customer, returning the persisted row. Suppresses the
     * global loading indicator so the send never blocks the screen; the composer reflects the in-flight
     * send by disabling the input and the send control while its `sending` state is set.
     */
    send(tenantId: string, customerId: string, input: CustomerTextInput): Observable<CustomerText> {
        return this.http
            .post<CustomerTextWire>(`${this.serviceUtils.getBaseUrl('customertext', tenantId)}/customer/${customerId}`, input, {
                headers: this.httpHeadersWithNoLoading,
            })
            .pipe(map(CustomerTextApiService.toCustomerText));
    }

    /**
     * Mints a fresh, time-limited link for a single attachment on demand. Recent
     * messages arrive from the thread with a populated attachment `url`; attachments older than the
     * server-side window arrive with `url` null, and the UI calls this only when the viewer asks to open
     * one -- so a link is minted lazily, per attachment, instead of for every attachment on every load.
     * A background read -- suppresses the global loading indicator; the placeholder tile shows its own
     * in-flight state. A `404` (link unavailable) surfaces through the caller's error path.
     */
    getAttachmentMedia(tenantId: string, attachmentId: string): Observable<string> {
        return this.http
            .get<{ url: string }>(`${this.serviceUtils.getBaseUrl('customertext', tenantId)}/attachments/${attachmentId}/media`, {
                headers: this.httpHeadersWithNoLoading,
            })
            .pipe(map((response) => response.url));
    }

    /**
     * Marks a message read, returning the updated row so the bubble's read-by tooltip and status
     * refresh in place. Fired in the background as messages are viewed -- suppresses the global
     * loading indicator.
     */
    markRead(tenantId: string, id: string): Observable<CustomerText> {
        return this.http
            .post<CustomerTextWire>(
                `${this.serviceUtils.getBaseUrl('customertext', tenantId)}/${id}/read`,
                {},
                {
                    headers: this.httpHeadersWithNoLoading,
                },
            )
            .pipe(map(CustomerTextApiService.toCustomerText));
    }

    /** Converts a wire `CustomerText` (ISO 8601 date strings) into the typed model with `Date` fields. */
    private static toCustomerText(wire: CustomerTextWire): CustomerText {
        return {
            ...wire,
            sentDate: new Date(wire.sentDate),
            readDate: wire.readDate ? new Date(wire.readDate) : null,
        };
    }

    /** Converts a wire `CustomerTextConversation` (ISO 8601 `latestSentDate`) into the typed model with a `Date`. */
    private static toConversation(wire: CustomerTextConversationWire): CustomerTextConversation {
        return {
            ...wire,
            latestSentDate: new Date(wire.latestSentDate),
        };
    }
}
